import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography,
  IconButton, Box, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import firebase from '../utilities/firebase';
import { StateManager } from '../utilities/stateManager';
import { useStackZIndex, ModalStackProvider } from '../utilities/ModalStackContext';
import { WriteCheckButton, ViewCheckButton } from './WriteCheckButton';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

export default function ExpenseModal({ open, onClose, expense, stockNumber, onSaved }) {
  const myZ = useStackZIndex();
  const isCreate = !expense;
  const canEdit = isCreate || StateManager.isManager();
  const [form, setForm] = React.useState({});
  const [file, setFile] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (expense) {
      setForm({
        paidDate: expense.paidDate || '',
        stock: expense.stock || '',
        vendor: expense.vendor || '',
        amount: expense.amount != null ? String(expense.amount) : '',
        memo: expense.memo || '',
        isPayable: String(expense.isPayable ?? true),
        type: expense.type || '',
        account: expense.account || '',
      });
    } else {
      setForm({
        paidDate: moment().format('YYYY/MM/DD'),
        vendor: '',
        amount: '',
        memo: '',
        isPayable: 'true',
      });
      setFile(null);
    }
  }, [expense, open]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    if (field === 'isPayable' && value === 'false') {
      setForm(prev => ({ ...prev, isPayable: value, paidDate: moment().format('YYYY/MM/DD') }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isCreate) {
        const docRef = firebase.firestore().collection('purchases').doc();
        const data = {
          e: docRef.id,
          stock: stockNumber,
          account: 'repair',
          type: 'sales',
          date: moment().format('YYYY/MM/DD'),
          paidDate: form.isPayable === 'false' ? form.paidDate : '',
          vendor: form.vendor,
          amount: parseFloat(form.amount) || 0,
          memo: form.memo,
          isPayable: form.isPayable === 'true',
        };
        if (file) {
          const location = `receipts/${uuidv4()}`;
          await firebase.storage().ref(location).put(file);
          data.files = [location];
        }
        await docRef.set(data);
        StateManager.setAlertAndOpen('Expense added', 'success');
        if (onSaved) onSaved({ ...data, id: docRef.id });
        onClose();
      } else {
        const update = {
          ...form,
          amount: parseFloat(form.amount) || 0,
          isPayable: form.isPayable === 'true',
        };
        await firebase.firestore().collection('purchases').doc(expense.id).update(update);
        StateManager.setAlertAndOpen('Expense updated', 'success');
        if (onSaved) onSaved({ ...expense, ...update });
        onClose();
      }
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen(isCreate ? 'Failed to add expense' : 'Failed to update expense', 'error');
    }
    setSaving(false);
  };

  const showReceipt = async () => {
    const url = await firebase.storage().ref(expense.files[0]).getDownloadURL();
    window.open(url, '_blank');
  };

  return (
    <ModalStackProvider value={myZ}>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: myZ }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isCreate ? 'Add Expense' : (canEdit ? 'Edit Expense' : 'View Expense')}
          </Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth size="small" label="Paid Date"
              value={form.paidDate || ''} onChange={handleChange('paidDate')}
              disabled={!canEdit}
            />
          </Grid>
          {!isCreate && (
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" label="Stock Number"
                value={form.stock || ''} onChange={handleChange('stock')}
                disabled={!canEdit}
              />
            </Grid>
          )}
          <Grid item xs={isCreate ? 12 : 6}>
            <TextField
              fullWidth size="small" label="Vendor"
              value={form.vendor || ''} onChange={handleChange('vendor')}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth size="small" label="Amount" type="number"
              value={form.amount || ''} onChange={handleChange('amount')}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="Memo"
              value={form.memo || ''} onChange={handleChange('memo')}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Paid?
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                fullWidth
                value={form.isPayable || 'true'}
                onChange={(_, v) => v != null && handleChange('isPayable')({ target: { value: v } })}
                disabled={!canEdit}
              >
                <ToggleButton value="false">Yes (Paid)</ToggleButton>
                <ToggleButton value="true">No (Unpaid)</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
          {!isCreate && StateManager.isAdmin() && (
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Type"
                value={form.type || ''} onChange={handleChange('type')}
                disabled={!canEdit}
              />
            </Grid>
          )}
          {isCreate ? (
            <Grid item xs={12}>
              <Button variant="outlined" size="small" component="label" startIcon={<AttachFileIcon />}>
                {file ? file.name : 'Attach Receipt (optional)'}
                <input type="file" hidden onChange={e => setFile(e.target.files[0] || null)} />
              </Button>
            </Grid>
          ) : expense?.files?.length > 0 && (
            <Grid item xs={12}>
              <Button variant="outlined" size="small" startIcon={<ReceiptIcon />} onClick={showReceipt}>
                View Receipt
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {!isCreate && <ViewCheckButton purchase={expense} iconOnly={false} />}
          {!isCreate && expense?.isPayable !== false && !expense?.checkMade && (
            <WriteCheckButton purchases={expense} />
          )}
        </Box>
        <Box>
          <Button onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : (isCreate ? 'Add' : 'Save')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
    </ModalStackProvider>
  );
}
